import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../context/FiltersContext.tsx';
import type { RangeConfigurableFilter, RangeSelectorValue } from '../types.ts';
import { useRangeValidation } from './useRangeValidation.ts';

export const useRangeState = (
  filterKey: string,
  selectedOptions: [string | undefined, string | undefined] | undefined,
  onOptionChange: (value?: [string | undefined, string | undefined]) => void,
  config?: RangeConfigurableFilter<string>['config'],
  setValidationState?: (value: boolean) => void
) => {
  const { t } = useTranslation();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [min, setMin] = useState(selectedOptions?.[0] ?? '');
  const [max, setMax] = useState(selectedOptions?.[1] ?? '');
  const [errors, setErrors] = useState<RangeSelectorValue>({});
  const { validate } = useRangeValidation(config);
  const { setFiltersErrors } = useFiltersContext();

  const handleChange = useCallback(
    (key: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const newMin = key === 'min' ? value : min;
      const newMax = key === 'max' ? value : max;

      setMin(newMin);
      setMax(newMax);

      const validationOfFields = validate(newMin, newMax, t);

      setErrors(validationOfFields);

      const normalized: [string | undefined, string | undefined] = [
        newMin.trim() || undefined,
        newMax.trim() || undefined,
      ];

      const hasValues = normalized.some(Boolean);

      if (isEmpty(validationOfFields)) {
        const debounce = config?.debounce;

        if (typeof debounce === 'number') {
          if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
          }

          debounceRef.current = setTimeout(() => onOptionChange(hasValues ? normalized : undefined), debounce);
        } else {
          onOptionChange(hasValues ? normalized : undefined);
        }
      }
    },
    [min, max, onOptionChange, validate, t, config?.debounce]
  );

  useEffect(() => {
    if (Object.keys(errors).length) {
      setValidationState?.(true);
    } else {
      setValidationState?.(false);
    }
  }, [errors, setValidationState]);

  useEffect(() => {
    setFiltersErrors(prev => ({
      ...prev,
      [filterKey]: errors.min || errors.max ? { ...errors } : undefined,
    }));
  }, [errors, filterKey, setFiltersErrors]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    min,
    max,
    errors,
    handleChange,
  };
};
